/* eslint-disable prefer-const */
import _ from 'lodash';
import { WoodOutlinedColoredBeam } from './ModelElements/WoodOutlinedColoredBeam';
import FiberText from './FiberText/FiberText2D';

const DEFAULT_FRONT_PLANE = 8.5;
const DEFAULT_ORDER_COLOR = '#7f76ffff';

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

function createLamellaData(Component, width, thickness, length, position, rotation, key, color) {
  return {
    component: Component,
    scale: [width / 100, thickness / 10000, length / 5000],
    position,
    rotation,
    key,
    color
  };
}

// function getQualityText(index, beamBuild) {
//   return beamBuild[index] || 'ni';
// }

function adjustQualityColors(seq, beamBuild, stackedLamellas) {
  // Optional: Use stackedLamellas to limit how many items from seq we process.
  const toProcess = stackedLamellas ? seq.slice(0, stackedLamellas) : seq;

  // Map each lamella's quality to the index in beamBuild
  return toProcess.map((lamella) => {
    const qualityIndex = beamBuild.indexOf(lamella.quality);

    // If not found, you can decide to return -1 or throw an error:
    if (qualityIndex === -1) {
      // console.warn(`Quality "${lamella.quality}" not found in beamBuild array.`);
      return -1; // or some fallback
    }
    return qualityIndex;
  });
}

// function getBeamSequence(jd, beamBuild) {
//   if (!Array.isArray(beamBuild)) {
//     throw new Error('beamBuild should be an array');
//   }

//   const { numOfLamellas } = jd;
//   const sequence = [];
//   const order = beamBuild.map((quality) => {
//     if (jd[quality] === undefined) {
//       throw new Error(`Property ${quality} is not defined in bshlayer`);
//     }
//     return jd[quality];
//   });

//   const total = order.reduce((acc, val) => acc + val, 0);
//   let perLamella = Math.floor(numOfLamellas / total);
//   let remainder = numOfLamellas % total;

//   for (let i = 0; i < order.length; i++) {
//     let count = order[i] * perLamella;
//     if (remainder > 0) {
//       count += 1;
//       remainder -= 1;
//     }
//     for (let j = 0; j < count; j++) {
//       sequence.push(i);
//     }
//   }
//   console.log('sequence: ', sequence);
//   return sequence;
// }

function getColorFromString(index, colors) {
  const color = colors[index % colors.length];
  return color?.substring(1); // Return color without #
}

function normalizeOrderColor(orderColor) {
  if (typeof orderColor !== 'string') {
    return DEFAULT_ORDER_COLOR;
  }
  const hexOnly = orderColor.trim().replace(/[^0-9a-fA-F]/g, '');
  const sanitized = hexOnly.length >= 6 ? hexOnly.slice(0, 6) : hexOnly;
  return sanitized.length > 0 ? sanitized : DEFAULT_ORDER_COLOR;
}

export default function getBodenlagerStackModel(order, jd, id, beamOptions) {
  const lamellas = [];
  const beamLayer = jd;

  /* Beam options from config */
  const beamBuild = _.get(beamOptions, 'beamBuild', null);
  // const beamBuildText = _.get(beamOptions, 'beamBuildText', null);
  const beamColors = _.get(beamOptions, 'beamColors', null);
  // const textColors = _.get(beamOptions, 'textColors', null);
  const alignPointStart = Number(_.get(beamOptions, 'alignPointStart', Number.NaN));
  const lengthScaleRatioRaw = _.get(beamOptions, 'lengthScaleRatio', 1);
  const parsedLengthScaleRatio = Number(lengthScaleRatioRaw);
  const lengthScaleRatio = Number.isFinite(parsedLengthScaleRatio) && parsedLengthScaleRatio > 0 ? parsedLengthScaleRatio : 1;
  const widthScaleMultiplierRaw = _.get(beamOptions, 'widthScaleMultiplier', 1);
  const parsedWidthScaleMultiplier = Number(widthScaleMultiplierRaw);
  const widthScaleMultiplier = Number.isFinite(parsedWidthScaleMultiplier) && parsedWidthScaleMultiplier > 0 ? parsedWidthScaleMultiplier : 1;
  const stackFrontInsetRatio = toFiniteNumber(_.get(beamOptions, 'stackFrontInsetRatio', 0));
  const stackFrontInset = toFiniteNumber(_.get(beamOptions, 'stackFrontInset', 0));
  const frontPlaneConfigValue = _.get(beamOptions, 'frontPlane', undefined);
  const parsedFrontPlane = Number(frontPlaneConfigValue);
  const hasFrontPlaneOverride = frontPlaneConfigValue !== undefined && frontPlaneConfigValue !== null && Number.isFinite(parsedFrontPlane);
  const frontPlane = hasFrontPlaneOverride ? parsedFrontPlane : DEFAULT_FRONT_PLANE;
  const compartmentIndex = Number.isFinite(id) ? id : 0;
  const mapRoot = _.get(order, 'map', order);
  const stackPath = _.get(order, 'stackPath', 'stackStorage');
  const stackIndex = _.get(order, 'stackIndex', null);
  let stackSource = _.get(mapRoot, stackPath);
  if (Number.isFinite(stackIndex)) {
    stackSource = Array.isArray(stackSource) ? stackSource[stackIndex] : stackSource?.[stackIndex];
  }
  let compartments = stackSource?.compartments;
  if (!Array.isArray(compartments) && Array.isArray(stackSource)) {
    compartments = stackSource[0]?.compartments || stackSource;
  }
  const compartmentColor = normalizeOrderColor(_.get(compartments, `[${compartmentIndex}].orderColor`, _.get(order, 'stackStorage[0].compartments[0].orderColor', DEFAULT_ORDER_COLOR)));

  /* Selected layer */
  if (beamLayer) {
    // console.log('beamSingleLayer: ', beamLayer);
    // let beamSequence = getBeamSequence(jd, beamBuild);
    let beamSequence = adjustQualityColors(jd.beamSequence, beamBuild, jd.numOfLamellas);

    let lamellaBuild = [(beamLayer.lamellaWidth / 10) * widthScaleMultiplier]; // Extract widths from widthoffset
    let totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);
    // console.log('lamellaBuild: ', lamellaBuild, ' beamLayer.lamellaThickness: ', beamLayer.lamellaThickness, ' beamLayer.lamellaLength: ', beamLayer.lamellaLength);
    let n = 1;
    for (let nIndex = 0; nIndex < n; nIndex++) {
      if (lamellaBuild.length > 0) {
        let groupStartPosition = 0;
        const initialOffset = -totalWidth / 2;

        for (let j = 0; j < beamSequence.length; j++) {
          let offset = -totalWidth / 2;
          let reversed = (j + 1) % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;

          for (let i = 0; i < reversed.length; i++) {
            offset += reversed[i];
            // const currentText = getQualityText(beamSequence[j], beamBuildText);
            const currentText = beamSequence[j].quality;
            const lamellaLengthScaled = beamLayer.lamellaLength * 2.5 * lengthScaleRatio;
            const lamellaDepth = lamellaLengthScaled / 5000;
            let lamellaPositionZ;
            let lamellaFrontEdge;

            if (hasFrontPlaneOverride || Number.isFinite(alignPointStart)) {
              const lamellaFrontInset = stackFrontInset + lamellaDepth * stackFrontInsetRatio;
              lamellaPositionZ = frontPlane - lamellaFrontInset - lamellaDepth / 2;
              lamellaFrontEdge = lamellaPositionZ + lamellaDepth / 2;
            } else {
              const visualLength = beamLayer.lamellaLength * (32 / 24000) * lengthScaleRatio;
              const scaleZ = visualLength / 3.5;
              const zPos = -scaleZ;
              lamellaPositionZ = zPos + DEFAULT_FRONT_PLANE;
              lamellaFrontEdge = lamellaPositionZ + lamellaDepth / 2;
            }
            const maxInset = Math.max(0, lamellaDepth - 0.3);
            const labelInset = Math.min(Math.max(4, lamellaDepth * 0.6), maxInset);
            const isLastItem = j === beamSequence.length - 1 && i === reversed.length - 1;
            const baseBeamColor = Array.isArray(beamColors) ? getColorFromString(beamSequence[j], beamColors) : undefined;
            const beamColor = normalizeOrderColor(compartmentColor || baseBeamColor || DEFAULT_ORDER_COLOR);
            const textColor = `#${beamColor}`;

            lamellas.push(
              createLamellaData(
                WoodOutlinedColoredBeam,
                reversed[i],
                beamLayer.lamellaThickness * 2.5,
                lamellaLengthScaled,
                [offset / 100 + nIndex, j / beamSequence.length, lamellaPositionZ],
                0,
                `WoodOutlined_${nIndex}_${j}_${i}`,
                beamColor
              )
            );

            const groupEndPosition = isLastItem ? (j + 1) / beamSequence.length : (j + 1) / beamSequence.length;
            const textPosition = (groupStartPosition + groupEndPosition) / 2;

            lamellas.push({
              component: FiberText,
              position: [initialOffset / 200 + 0.3 + nIndex * 2, textPosition, 5],
              children: currentText,
              size: 0.8,
              key: `text-${nIndex}-${j}-${i}`,
              color: textColor
            });

            // Add lamella length text on top of the stack
            if (isLastItem) {
              const defaultLabel = `${beamSequence.length} lam. - ${beamLayer.lamellaPlannedThickness} x ${beamLayer.lamellaPlannedWidth} x ${beamLayer.lamellaLength} | CH: ${beamLayer.chargeNumber} Auf.: ${beamLayer.orderNumber}   JobN.: ${beamLayer.jobNumber}`;
              const template = beamLayer.labelTemplate;
              let stackLabel = defaultLabel;

              if (typeof template === 'function') {
                try {
                  stackLabel =
                    template({
                      beamLayer,
                      beamSequence,
                      beamOptions,
                      defaultLabel
                    }) ?? defaultLabel;
                } catch (error) {
                  console.warn('labelTemplate error:', error);
                }
              } else if (typeof template === 'string') {
                stackLabel = template;
              }

              lamellas.push({
                component: FiberText,
                position: [
                  initialOffset / 100 + nIndex, // start of lamella stack
                  groupEndPosition,
                  lamellaFrontEdge - labelInset
                ],
                rotation: [-Math.PI / 2, 0, -Math.PI / 2],
                size: 1,
                key: `length-text-${nIndex}-${j}`,
                color: 'black',
                billboard: false,
                children: stackLabel
              });
            }

            groupStartPosition = groupEndPosition;
            offset += reversed[i];
          }
        }
      }
    }
  }

  const lamellaMeshes = lamellas.filter(
    (entry) => entry?.component === WoodOutlinedColoredBeam && Array.isArray(entry?.position) && entry.position.length >= 3 && Array.isArray(entry?.scale) && entry.scale.length >= 3
  );

  if (!lamellaMeshes.length || !Number.isFinite(alignPointStart)) {
    return lamellas;
  }

  const maxRightEdge = Math.max(...lamellaMeshes.map((entry) => entry.position[2] + entry.scale[2] / 2));
  if (!Number.isFinite(maxRightEdge)) {
    return lamellas;
  }

  const offset = alignPointStart - maxRightEdge;
  if (Math.abs(offset) < 1e-6) {
    return lamellas;
  }

  return lamellas.map((entry) => {
    if (Array.isArray(entry?.position) && entry.position.length >= 3) {
      const [x, y, z] = entry.position;
      return {
        ...entry,
        position: [x, y, z + offset]
      };
    }
    return entry;
  });
}
